import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import SellerLayout from './components/SellerLayout';
import ScrollToTop from './components/ScrollToTop';
import { CountryProvider } from './components/CountryContext';
import { ShippingRatesProvider } from './hooks/useGlobalShippingRates';
import { CookieProvider } from './components/CookieContext';
import { useThirdPartyServices } from './hooks/useThirdPartyServices';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import SignUpCustomer from './pages/SignUpCustomer';
import CustomerAccount from './pages/CustomerAccount';
import SignUpSeller from './pages/SignUpSeller';
import SellerAwaitConfirmation from './pages/SellerAwaitConfirmation';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import SellerAccount from './pages/SellerAccount';
import SellerAnalytics from './pages/SellerAnalytics';
import SellerIntegration from './pages/SellerIntegration';
import SellerProducts from './pages/SellerProducts';
import ShippingRatesPage from './pages/ShippingRatesPage';
import MerchantPage from './pages/MerchantPage';
import ProtectedRoute from './components/ProtectedRoute';
import Logout from './pages/Logout';
import EmailVerified from './pages/EmailConfirmation';
import ResendConfirmationEmail from './pages/ResendConfirmationEmail';
import SearchResults from './pages/SearchResults';
import SellerGrid from './pages/SellerGrid';
import SellerProfilePage from './pages/SellerProfilePage';
import CategoryPage from './pages/CategoryPage';
import AllCategoriesPage from './pages/AllCategoriesPage';
import NotFoundPage from './pages/NotFoundPage';
import ProductPage from './pages/ProductPage';
import BrandsPage from './pages/BrandsPage';
import BrandProductsPage from './pages/BrandProductsPage';
import AdminVendorsPage from './pages/AdminVendorsPage';
import AdminCustomersPage from './pages/AdminCustomersPage';
import AdminMerchantPage from './pages/AdminMerchantPage';

import AdminLoverlistCssPage from './pages/AdminLoverlistCssPage';
import AdminMerchantAccountsPage from './pages/AdminMerchantAccountsPage';
import AdminGoogleAdsSpendingPage from './pages/AdminGoogleAdsSpendingPage';
import AdminBillingOverviewPage from './pages/AdminBillingOverviewPage';
import AdminTransactionsInvoicesPage from './pages/AdminTransactionsInvoicesPage';
import LoverlistPage from './pages/LoverlistPage';
import ContattiPage from './pages/ContattiPage';
import DiventaInserzionistaPage from './pages/DiventaInserzionistaPage';
import ComeFunzionaPage from './pages/ComeFunzionaPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SellerTermsOfServicePage from './pages/SellerTermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import GuideIntegrazioneCsv from './pages/GuideIntegrazioneCsv';
import SellerGoogleAdsCampaigns from './pages/SellerGoogleAdsCampaigns';
import SellerBillingPage from './pages/SellerBillingPage';
import { WishlistProvider } from './components/WishlistContext';

function App() {
  return (
    <CookieProvider>
      {/* Gestisce automaticamente tutti i servizi di terze parti (analytics, marketing, etc.)
          basandosi sulle preferenze dei cookie */}
      <AppContent />
    </CookieProvider>
  );
}

// Componente separato per poter usare useThirdPartyServices dentro CookieProvider
function AppContent() {
  useThirdPartyServices();

  return (
    <WishlistProvider>
      <CountryProvider>
        <ShippingRatesProvider>
          <Router>
            <ScrollToTop />
            <Layout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/sign-up-customer" element={<SignUpCustomer />} />
                <Route path="/sign-up-seller" element={<SignUpSeller />} />
                <Route path="/sign-up-seller-await-confirmation" element={<SellerAwaitConfirmation />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/email-verified" element={<EmailVerified />} />
                <Route path="/resend-confirmation-email" element={<ResendConfirmationEmail />} />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/seller" element={<SellerGrid />} />
                <Route path="/seller/:storeHandle" element={<SellerProfilePage />} />
                <Route path="/categories" element={<AllCategoriesPage />} />
                <Route path="/categories/:handle" element={<CategoryPage />} />
                <Route path="/products/:id" element={<ProductPage />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/brands/:brand" element={<BrandProductsPage />} />
                <Route path="/contatti" element={<ContattiPage />} />
                <Route path="/diventa-inserzionista" element={<DiventaInserzionistaPage />} />
                <Route path="/come-funziona" element={<ComeFunzionaPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/seller-terms-of-service" element={<SellerTermsOfServicePage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/guide/integrazione-csv" element={<GuideIntegrazioneCsv />} />
                <Route path="/404" element={<NotFoundPage />} />

                {/* Rotte protette */}
                <Route
                  path="/seller-integration"
                  element={
                    <ProtectedRoute allowedRoles={['seller']}>
                      <SellerLayout>
                        <SellerIntegration />
                      </SellerLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/seller-products"
                  element={
                    <ProtectedRoute allowedRoles={['seller']}>
                      <SellerLayout>
                        <SellerProducts />
                      </SellerLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/seller-analytics"
                  element={
                    <ProtectedRoute allowedRoles={['seller']}>
                      <SellerLayout>
                        <SellerAnalytics />
                      </SellerLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shipping-rates"
                  element={
                    <ProtectedRoute allowedRoles={['seller']}>
                      <SellerLayout>
                        <ShippingRatesPage />
                      </SellerLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/merchant"
                  element={
                    <ProtectedRoute allowedRoles={['seller']}>
                      <SellerLayout>
                        <MerchantPage />
                      </SellerLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/google-ads-campaigns"
                  element={
                    <ProtectedRoute allowedRoles={['seller']}>
                      <SellerLayout>
                        <SellerGoogleAdsCampaigns />
                      </SellerLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute allowedRoles={['seller']}>
                      <SellerLayout>
                        <SellerBillingPage />
                      </SellerLayout>
                    </ProtectedRoute>
                  }
                />
                {/* Rotte accessibili sia ai seller che ai customer */}
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute
                      allowedRoles={['seller', 'customer']}
                      sellerComponent={(props) => (
                        <SellerLayout>
                          <SellerAccount {...props} />
                        </SellerLayout>
                      )}
                      customerComponent={CustomerAccount}
                    >
                      <div>Pagina condivisa tra seller e customer</div>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/vendors"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminVendorsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customers"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminCustomersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/merchant-connections"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminMerchantPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/loverlist-css"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminLoverlistCssPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/merchant-accounts"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminMerchantAccountsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/google-ads-spending"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminGoogleAdsSpendingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/billing-overview"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminBillingOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/transactions-invoices"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminTransactionsInvoicesPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/wishlist" element={<LoverlistPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Layout>
          </Router>
        </ShippingRatesProvider>
      </CountryProvider>
    </WishlistProvider>
  );
}

export default App;
